import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import UserContext, { useIsLoggedIn } from '~/context/common/UserContext';
import type { FavoritesArguments, OnContinue } from './types';
import useAddFavoriteFlow from './useAddFavoriteFlow';
import useRemoveFavorite from './useRemoveFavorite';

type Options = {
  onAddFavorite?: OnContinue;
};

export default function useToggleFavorite({ onAddFavorite }: Options = {}): (
  args: FavoritesArguments
) => void {
  const {
    favorites: { data: favorites }
  } = useContext(UserContext);
  const isLoggedIn = useIsLoggedIn();

  const favoritesIds = useMemo(
    () => favorites.map(v => v.vehicleId),
    [favorites]
  );
  const [localFavoritesIds, setLocalFavoritesIds] = useState(favoritesIds);

  useEffect(() => {
    setLocalFavoritesIds(favoritesIds);
  }, [favoritesIds]);

  const addFavorite = useAddFavoriteFlow({ onAddFavorite });
  const removeFavorite = useRemoveFavorite();

  return useCallback(
    (args: FavoritesArguments) => {
      const shouldAdd = !localFavoritesIds.includes(args.vehicleId);
      const doAction = shouldAdd ? addFavorite : removeFavorite;

      doAction(args);

      if (isLoggedIn) {
        setLocalFavoritesIds(
          shouldAdd
            ? localFavoritesIds.concat(args.vehicleId)
            : v => v.filter(vid => vid !== args.vehicleId)
        );
      }
    },
    [addFavorite, isLoggedIn, localFavoritesIds, removeFavorite]
  );
}
